<template>
  <div class="overflow-hidden rounded-lg bg-gray-50 pb-4 sm:pb-1">
    <div class="px-1 md:px-2 py-2">
      <div class="lg:flex lg:h-full lg:flex-col">
        <header class="flex items-center justify-between border-b border-gray-200 py-4 lg:flex-none">
          <h1 class="text-base font-semibold leading-6 text-gray-900">
            {{title}} - <time :datetime="formattedYearMonth">{{ formattedYearMonthDisplay }}</time>
          </h1>
          <div class="flex items-center">
            <div class="relative flex items-center shadow rounded-md bg-white md:items-stretch">
              <button
                type="button"
                class="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
                @click="previousMonth"
              >
                <span class="sr-only">Mes anterior</span>
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fill-rule="evenodd"
                    d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
                @click="goToToday"
              >
                {{$t('today')}}
              </button>
              <span class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"></span>
              <button
                type="button"
                class="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
                @click="nextMonth"
              >
                <span class="sr-only">Mes siguiente</span>
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fill-rule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div class="hidden md:ml-4 md:flex md:items-center shadow">
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  id="menu-button"
                  aria-expanded="false"
                  aria-haspopup="true"
                  @click="toggleViewMenu"
                >
                  {{ currentViewLocale }}
                  <svg class="-mr-1 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                      fill-rule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <div
                  v-if="showViewMenu"
                  class="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="menu-button"
                  tabindex="-1"
                >
                  <div class="py-1" role="none">
                    <router-link
                      :to="`${calendarModeProp.dailyTargetLink}`"
                      class="block px-4 py-2 text-sm text-gray-700"
                      role="menuitem"
                      tabindex="-1"
                    >Diaria</router-link>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                aria-expanded="false"
                aria-haspopup="true"
                @click="reloadCalendar"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span class="sr-only">Recargar calendario</span>
              </button>
              <!-- <div class="ml-6 h-6 w-px bg-gray-300"></div>
              <button
                type="button"
                class="ml-6 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                @click="addEvent"
              >
                Add event
              </button> -->
            </div>
            <div class="relative ml-6 md:hidden">
              <button
                type="button"
                class="flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500"
                id="menu-0-button"
                aria-expanded="false"
                aria-haspopup="true"
                @click="toggleMobileMenu"
              >
                <span class="sr-only">Abrir menu</span>
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M3 10a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM8.5 10a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM15.5 8.5a.75.75 0 100 1.5.75.75 0 000-1.5z" />
                </svg>
              </button>
              <div
                v-if="showMobileMenu"
                class="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-0-button"
                tabindex="-1"
              >
                <!-- <div class="py-1" role="none">
                  <a
                    href="#"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    @click.prevent="addEvent"
                  >Create event</a>
                </div> -->
                <div class="py-1" role="none">
                  <a
                    href="#"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                    @click.prevent="goToToday"
                  >Hoy</a>
                </div>
                <div class="py-1" role="none">
                  <router-link
                    :to="`${calendarModeProp.dailyTargetLink}`"
                    class="block px-4 py-2 text-sm text-gray-700"
                    role="menuitem"
                    tabindex="-1"
                  >Diaria</router-link>
                </div>
              </div>
            </div>
          </div>
        </header>
        <div class="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
          <div class="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
            <div class="flex justify-center bg-white py-2" v-for="(day, index) in daysOfWeek" :key="index">
              <span>{{ day.short }}</span>
              <span class="sr-only sm:not-sr-only">{{ day.full }}</span>
            </div>
          </div>
          <div class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
            <div class="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
              <div
                v-for="(day, index) in daysInMonth"
                :key="index"
                :class="['relative', 'px-3', 'py-2', day.isCurrentMonth ? 'bg-white' : 'bg-gray-50', day.isToday ? 'text-gray-900 font-semibold' : 'text-gray-500']"
              >
                <router-link :to="`${calendarModeProp.dailyTargetLink}?cd=${encodeCurrentDate(day.date)}`">
                  <time
                    :dateTime="day.date"
                    :class="[ day.isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white' : undefined ]"
                  >
                    {{ day.day }}
                  </time>
                </router-link>
                <ol class="mt-2">
                  <template v-if="isTableBusy">
                    <li v-for="n in 2" :key="n">
                      <div class="group flex text-right animate-pulse">
                        <div class="flex-auto h-4 bg-gray-200 rounded"></div>
                        <div class="ml-3 hidden w-16 h-4 bg-gray-200 rounded xl:block"></div>
                      </div>
                    </li>
                  </template>
                  <template v-else>
                    <li v-for="(event, index) in day.events" :key="index">
                      <router-link :to="`${calendarModeProp.eventEditLink}${event.id}`" class="group flex text-right">
                        <p class="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600" v-html="event.name"></p>
                        <time v-if="event?.datetime" :datetime="event.datetime" class="ml-3 hidden flex-none text-gray-500 group-hover:text-indigo-600 xl:block">{{event?.short_datetime || event.datetime}}</time>
                        <span v-if="event?.counter" v-html="event.counter" class="calendar-counter ml-3 hidden flex-none text-gray-500 group-hover:text-indigo-600 xl:block"></span>
                      </router-link>
                    </li>
                  </template>
                </ol>
              </div>
            </div>
            <div class="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
              <template v-if="isTableBusy">
                <div v-for="n in 42" :key="n"
                     class="flex h-14 flex-col bg-white py-2 px-3">
                  <div class="ml-auto h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                  <span class="-mx-0.5 mt-auto flex flex-wrap-reverse">
                    <span class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-200 animate-pulse"></span>
                    <span class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-200 animate-pulse"></span>
                  </span>
                </div>
              </template>
              <template v-else>
                <button
                  v-for="(day, index) in daysInMonth"
                  :key="index"
                  type="button"
                  :class="['flex h-14 flex-col py-2 px-3 hover:bg-gray-100 focus:z-10', day.isCurrentMonth ? 'bg-white' : 'bg-gray-50', day.isSelected ? 'font-semibold text-white' : 'text-gray-500', day.isToday ? 'font-semibold text-indigo-600' : 'text-gray-900']"
                >
                  <router-link :to="`${calendarModeProp.dailyTargetLink}?cd=${encodeCurrentDate(day.date)}`">
                    <time :datetime="day.date" class="ml-auto">{{ day.day }}</time>
                    <span class="sr-only">{{ day.events.length }} Eventos</span>
                    <span class="-mx-0.5 mt-auto flex flex-wrap-reverse" v-if="day.events.length">
                      <span class="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-400" v-for="(event, index) in day.events" :key="index"></span>
                    </span>
                  </router-link>
                </button>
              </template>
            </div>
          </div>
        </div>
        <!-- Mobile section -->
        <div class="px-0 py-2 lg:hidden">
          <ol class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black ring-opacity-5">
            <template v-if="isTableBusy">
              <li class="group flex p-4 pr-6 animate-pulse">
                <div class="flex-auto">
                  <div class="h-5 bg-gray-200 rounded w-3/4"></div>
                  <div class="mt-2 flex items-center">
                    <div class="mr-2 h-5 w-5 rounded-full bg-gray-200"></div>
                    <div class="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                <div class="w-20 h-5 bg-gray-200 rounded"></div>
              </li>
            </template>
            <template v-else>
              <li v-for="(event, index) in allEvents" :key="index" class="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50">
                <div class="flex-auto">
                  <p class="font-semibold text-gray-900" v-html="event.name">
                  </p>
                  <time :datetime="event.datetime" class="mt-2 flex items-center text-gray-700">
                    <svg class="mr-2 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clip-rule="evenodd" />
                    </svg>
                    {{ event.date }}
                    {{ event.time }}
                  </time>
                </div>
                <router-link :to="`${calendarModeProp.eventEditLink}${event.id}`" class="text-sm font-semibold text-indigo-600">
                  {{ $t('view_more') }}
                </router-link>
              </li>
            </template>
          </ol>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  props: {
    title: {
      type: String,
      default: ''
    },
    events: {
      type: Array,
      default: () => []
    },
    calendarModeProp: {
      type: Object,
      default: () => ({})
    },
    calendarQuery: {
      type: Object
    },
    isTableBusy: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentDate: new Date(),
      currentView: 'Month',
      showViewMenu: false,
      showMobileMenu: false
    }
  },
  watch: {
    calendarQuery: {
      handler (newQuery, oldQuery) {
        if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
          this.changeCalendarQuery(newQuery)
        }
      }
    }

  },
  computed: {
    currentViewLocale () {
      switch (this.currentView) {
        case 'Day':
          return 'Día'
        case 'Week':
          return 'Semana'
        case 'Month':
          return 'Mensual'
        default:
          return 'Año'
      }
    },
    formattedYearMonth () {
      return this.currentDate.toISOString().substr(0, 7)
    },
    formattedYearMonthDisplay () {
      // help me return the date capitalized month name in Spanish
      return this.capitalize(this.currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }))
    },
    daysOfWeek () {
      const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] // Lunes a Domingo

      return days.map((day, index) => ({
        short: day, // Día abreviado
        full: moment.weekdays(index + 1).slice(1) // Nombre completo del día en español
      }))
    },
    daysInMonth () {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      const firstDayOfMonth = new Date(year, month, 1)
      const lastDayOfMonth = new Date(year, month + 1, 0)

      const days = []

      // Ajustar el primer día de la semana para que comience en lunes
      const startDay = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1

      // Agregar días vacíos antes del inicio del mes
      for (let i = 0; i < startDay; i++) {
        days.push({
          date: null,
          day: null,
          isCurrentMonth: false,
          isToday: false,
          events: []
        })
      }

      // Agregar los días del mes
      for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
        const day = new Date(year, month, i)
        days.push({
          date: day.toISOString().substr(0, 10),
          day: i,
          isCurrentMonth: true,
          isToday: this.isToday(day),
          events: this.getEventsForDay(day)
        })
      }

      // Completar hasta 42 días para cubrir 6 semanas
      const remainingDays = 42 - days.length
      for (let i = 1; i <= remainingDays; i++) {
        const day = new Date(lastDayOfMonth)
        day.setDate(lastDayOfMonth.getDate() + i)
        days.push({
          date: day.toISOString().substr(0, 10),
          day: day.getDate(),
          isCurrentMonth: false,
          isToday: this.isToday(day),
          events: this.getEventsForDay(day)
        })
      }

      return days
    },

    allEvents () {
      return this.events.reduce((acc, day) => {
        return acc.concat(day.itemDetails.map(event => ({
          ...event,
          date: day.date
        })))
      }, [])
    }
  },
  methods: {
    encodeCurrentDate (day) {
      return encodeURIComponent(day)
    },
    /**
     * capitalize the first letter of a string
     */
    capitalize (str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    reloadCalendar () {
      this.$emit('onReload')
    },
    emitReloadCalendar () {
      this.$emit('onReloadCalendar', {
        currentDate: this.currentDate,
        currentView: this.currentView
      })
    },
    previousMonth () {
      this.currentDate.setMonth(this.currentDate.getMonth() - 1)
      this.currentDate = new Date(this.currentDate).toISOString() // force reactivity
      this.emitReloadCalendar()
    },
    nextMonth () {
      this.currentDate.setMonth(this.currentDate.getMonth() + 1)
      this.currentDate = new Date(this.currentDate).toISOString() // force reactivity
      this.emitReloadCalendar()
    },
    goToToday () {
      this.currentDate = new Date()
      this.emitReloadCalendar()
    },
    toggleViewMenu () {
      this.showViewMenu = !this.showViewMenu
    },
    toggleMobileMenu () {
      this.showMobileMenu = !this.showMobileMenu
    },
    isToday (day) {
      const today = new Date()
      return (
        day.getDate() === today.getDate() &&
        day.getMonth() === today.getMonth() &&
        day.getFullYear() === today.getFullYear()
      )
    },
    getEventsForDay (day) {
      const date = day.toISOString().substr(0, 10)
      const dayEvents = this.events.find(event => event.date === date)
      return dayEvents ? dayEvents.itemDetails : []
    },
    addEvent () {
      // logic to add event
      alert('Add event clicked')
    },
    changeCalendarQuery (calendarQuery) {
      if (calendarQuery) {
        this.currentDate = new Date(calendarQuery.currentDate) // set current date from query
        // validate that current view is only 'Month' or 'Day' (default to 'Month')
        if (!['Month', 'Day'].includes(calendarQuery.currentView)) {
          this.currentView = 'Month'
        } else {
          this.currentView = calendarQuery.currentView
        }
      }
    }
  },

  created () {
    this.changeCalendarQuery(this.calendarQuery)
  }
}
</script>

<style>
.calendar-counter span {
  margin-right: 2px;
}

.calendar-counter span i {
  font-size: 15px !important;
}

.tooltip-custom {
  z-index: 100;
}

.tooltip-custom::before {
  content: attr(data-bs-original-title);
  position: absolute;
  white-space: nowrap;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #fff;
  padding: 3px;
  border-radius: 5px;
  font-size: 0.675rem;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-custom:hover::before {
  visibility: visible;
  opacity: 1;
}
</style>
